// Minimal client-side router. Pattern-matching against window.location.pathname.
// Routes register Component factories; auth guard redirects when needed.

import { LOCALE_EVENT } from './i18n/locale';

type RouteFactory = (params: Record<string, string>) => Component;
// A guard either allows the route, redirects elsewhere, or renders a screen in
// place (keeping the URL) — the last is how a capability denial shows
// the friendly forbidden surface instead of bouncing the user.
type GuardResult =
  | { allowed: true }
  | { allowed: false; redirectTo: string }
  | { allowed: false; render: RouteFactory };
type Guard = (path: string) => GuardResult;

export interface Component {
  mount(host: HTMLElement): void;
  unmount?(): void;
}

type Route = {
  pattern: RegExp;
  paramNames: string[];
  factory: RouteFactory;
  isPublic: boolean;
};

const routes: Route[] = [];
let guard: Guard | null = null;
let host: HTMLElement | null = null;
let active: Component | null = null;
let notFoundFactory: RouteFactory = () => textComponent('Not Found');

export function registerRoute(path: string, factory: RouteFactory, isPublic = false): void {
  const paramNames: string[] = [];
  const re = new RegExp(
    '^' +
      path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name) => {
        paramNames.push(name);
        return '([^/]+)';
      }) +
      '/?$'
  );
  routes.push({ pattern: re, paramNames, factory, isPublic });
}

export function setNotFound(factory: RouteFactory): void {
  notFoundFactory = factory;
}

export function setAuthGuard(g: Guard): void {
  guard = g;
}

export function initRouter(mountPoint: HTMLElement): void {
  host = mountPoint;
  window.addEventListener('popstate', renderRoute);
  // A locale change re-renders the active route in place: the whole tree
  // (shell + nav + page) is rebuilt, so every keyed string picks up the new
  // locale without per-component listeners. The URL/path is unchanged.
  window.addEventListener(LOCALE_EVENT, renderRoute);
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement | null)?.closest?.('a[href^="/"]');
    if (!target) return;
    const href = (target as HTMLAnchorElement).getAttribute('href');
    if (!href || href.startsWith('//')) return;
    if ((target as HTMLAnchorElement).target === '_blank') return;
    e.preventDefault();

    const hashIdx = href.indexOf('#');
    if (hashIdx !== -1) {
      const path = href.slice(0, hashIdx) || '/';
      const hash = href.slice(hashIdx + 1);
      if (location.pathname !== path) {
        navigate(path);
      }
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: 'smooth' });
      });
      return;
    }
    navigate(href);
  });
  renderRoute();
}

export function navigate(path: string): void {
  if (location.pathname + location.search !== path) {
    history.pushState(null, '', path);
  }
  renderRoute();
}

function renderRoute(): void {
  if (!host) return;
  const mountPoint = host;
  const path = location.pathname;

  const mount = (factory: RouteFactory, params: Record<string, string>): void => {
    if (active && active.unmount) active.unmount();
    mountPoint.innerHTML = '';
    active = factory(params);
    active.mount(mountPoint);
  };

  if (guard) {
    const result = guard(path);
    if (!result.allowed) {
      if ('render' in result) {
        mount(result.render, {});
        return;
      }
      if (path !== result.redirectTo) {
        navigate(result.redirectTo);
        return;
      }
    }
  }

  const match = routes.find((r) => r.pattern.test(path));
  const params: Record<string, string> = {};
  let factory: RouteFactory = notFoundFactory;
  if (match) {
    const captured = match.pattern.exec(path);
    if (captured) {
      match.paramNames.forEach((name, i) => {
        params[name] = decodeURIComponent(captured[i + 1] ?? '');
      });
    }
    factory = match.factory;
  }
  mount(factory, params);
}

function textComponent(text: string): Component {
  return {
    mount(h) {
      const p = document.createElement('p');
      p.textContent = text;
      p.style.padding = 'var(--space-xl)';
      h.appendChild(p);
    },
  };
}
